import ChannelGroupingPerformanceReportV2 from 'reportingSolution/reportTemplates/channelGroupingPerformanceReportv2'
import FacebookVisualCreativeReport from 'reportingSolution/reportTemplates/facebookVisualCreativeReport'
import GAdsKeywordQualityScore from 'reportingSolution/reportTemplates/gadsKeywordsQualityScore'
import AttributionComparisonReport from 'reportingSolution/reportTemplates/attributionComparisonReport'
import pmmReport from 'reportingSolution/reportTemplates/pmmReport'
import PMaxBreakdownReportV2 from 'reportingSolution/reportTemplates/pmaxBreakdownReportV2'
import dv360performanceReport from 'reportingSolution/reportTemplates/dv360PerformanceReport'
import blackFridayBaseReport from 'reportingSolution/reportTemplates/blackFridayBaseReport'
import ArimaPlusReport from 'reportingSolution/reportTemplates/arimaPlusReport'
import ReportingBaseReport from 'reportingSolution/reportTemplates/reportingBaseReport'
import SearchSynergiesReport from 'reportingSolution/reportTemplates/searchSynergiesReport'
import CreativeInsightsReport from 'reportingSolution/reportTemplates/creativeInsightsReport'
import ShoppingInsightsV3Report from 'reportingSolution/reportTemplates/shoppingInsightsV3Report'
import ShoppingBestsellerV2Report from 'reportingSolution/reportTemplates/shoppingBestsellerV2Report'
import { currencies } from 'shared/constants/currency'
import { type InlineChannelGroupingChannel } from 'reportingSolution/components/CaseStatementBuilder/types'
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils'

export const GA4_MEASUREMENT_ID = 'G-F3BRHF1KER'

export const DEFAULT_BQ_DATASET = 'alvie_reporting_solutions'

export const DEFAULT_CURRENCY_OPTIONS = currencies.map((currency) => ({
  value: currency.code,
  label: `${currency.name} (${currency.code})`
}))

interface Sources {
  reports: string[]
  ia_configs?: object[]
  optional?: boolean
  hidden?: boolean
  internalOnly?: boolean
}

interface DropdownOption {
  value: string
  label: string
  labelAnnotation?: string
  icon_url?: string
  img_src?: string
  group?: string
}

export interface CaseStatementDimension {
  value: string
  label: string
  type: string
}

interface InputValueField {
  label: string
  id: string
}

export interface InputSchema {
  id: string
  type:
    | 'string'
    | 'dropdown'
    | 'date'
    | 'integer'
    | 'case_statement'
    | 'json_object_array'
    | 'custom_grouping_or_case_statement'
    | 'boolean'
    | 'alvie_attribution_model_selector'
  options?: DropdownOption[]
  dimensions?: CaseStatementDimension[]
  defaultValue?: string | number | string[] | boolean | InlineChannelGroupingChannel[]
  defaultValues?: object[]
  label: string
  helperText?: string
  tooltip?: string
  required: boolean
  mutable: boolean
  inputFormat?: string
  multiple?: boolean
  inputValueFields?: InputValueField[]
  minRange?: number
  maxRange?: number
  isRegex?: boolean
  internalOnly?: boolean
}

type InputSchemas = Record<string, InputSchema[]>

interface Documentation {
  type: string
  link: string
  link_description: string
}

interface Author {
  name: string
  email: string
  image_url: string
}

interface BaseDashboardDataSource {
  alias: string
  datasourceName: string
}

interface TableDashboardDataSource extends BaseDashboardDataSource {
  type: 'TABLE'
  tableId: string
  isPartitioned?: string
  refreshFields?: string
}

interface CustomDashboardDataSource extends BaseDashboardDataSource {
  type: 'CUSTOM'
  sqlReplace?: string
}

export type DashboardDataSource = TableDashboardDataSource | CustomDashboardDataSource

export interface DashboardSheetDataSource {
  alias: string
  hasHeader: string
  refreshFields: string
  helperText: string
  datasourceName: string
}

interface Dashboard {
  template_report_id: string
  report_name_template: string
  datasources: DashboardDataSource[]
  sheets_datasources?: DashboardSheetDataSource[]
}

export interface ReportTemplate {
  id: string
  active: boolean
  deprecation_date?: string
  deprecation_alternative_template_id?: string
  label: string
  category:
    | 'Getting started'
    | 'Forecasting'
    | 'Other'
    | 'Seasonal'
    | 'General'
    | 'Platform specific'
    | 'Google Analytics 4 Export'
    | 'Product analysis'
    | 'Search'

  description: string
  image_url: string
  dashboard?: Dashboard
  authors: Author[]
  documentation: Documentation
  sources: Record<string, Sources>
  inputs?: InputSchemas
}

export const isReportSourceVisible = (
  sourceConfig: Sources | undefined,
  shouldShowInternalFeatures: boolean
): boolean => {
  if (sourceConfig == null) {
    return false
  }

  if (sourceConfig.internalOnly === true && !shouldShowInternalFeatures) {
    return false
  }

  if (sourceConfig.hidden === true) {
    return sourceConfig.internalOnly === true && shouldShowInternalFeatures
  }

  return true
}

export interface VisibleReportSourceOptions {
  shouldShowInternalFeatures?: boolean
  isCreativeInsightsGadsEnabled?: boolean
  excludeDeprecated?: boolean
}

export const getVisibleReportSourceKeys = (
  reportTemplate: ReportTemplate,
  shouldShowInternalFeaturesOrOptions: boolean | VisibleReportSourceOptions = true
): string[] => {
  const options: VisibleReportSourceOptions =
    typeof shouldShowInternalFeaturesOrOptions === 'boolean'
      ? { shouldShowInternalFeatures: shouldShowInternalFeaturesOrOptions }
      : shouldShowInternalFeaturesOrOptions
  const shouldShowInternalFeatures = options.shouldShowInternalFeatures ?? true
  const isCreativeInsightsGadsEnabled = options.isCreativeInsightsGadsEnabled ?? false
  const excludeDeprecated = options.excludeDeprecated ?? false

  return Object.keys(reportTemplate.sources).filter((source) => {
    if (reportTemplate.id === 'creative_insights_report' && source === 'gads' && !isCreativeInsightsGadsEnabled) {
      return false
    }
    if (excludeDeprecated && getPlatformDetailsByPlatform(source)?.isDeprecated === true) {
      return false
    }
    return isReportSourceVisible(reportTemplate.sources[source], shouldShowInternalFeatures)
  })
}

export const reportTemplateRegistry: Record<string, ReportTemplate> = {
  channel_grouping_performance_report_v2: ChannelGroupingPerformanceReportV2,
  facebook_visual_creative_report: FacebookVisualCreativeReport,
  gads_keywords_quality_score: GAdsKeywordQualityScore,
  pmax_breakdown_report_v2: PMaxBreakdownReportV2,
  pmm_report: pmmReport,
  attribution_comparison_report: AttributionComparisonReport,
  dv360_performance_report: dv360performanceReport,
  black_friday_base_report: blackFridayBaseReport,
  arima_plus_report: ArimaPlusReport,
  reporting_base: ReportingBaseReport,
  search_synergies_report: SearchSynergiesReport,
  creative_insights_report: CreativeInsightsReport,
  shopping_insights_v3: ShoppingInsightsV3Report,
  shopping_bestseller_report_v2: ShoppingBestsellerV2Report
}
